interface GetMobileMainWrapperClassNameArgs {
  isBottomHidden: boolean;
  isTopHidden: boolean;
}

/**
 * When a side is hidden, `MobileShell.Header`/`Footer` never mount, so
 * `MobileShell.Content` falls back to its own baseline + safe-area padding
 * on `<main>` for that side (see Compass's `headerHeight`/`footerHeight
 * === undefined` branches) - meant for pages with no chrome at all to
 * absorb the notch/home-indicator inset directly. Pages that hide a side
 * via useMobileHideChrome/useMobileHideTopNav/useMobileHideBottomNav
 * supply their own chrome on that side (own header, or none needed) that
 * already accounts for the safe area, so that fallback padding is just an
 * unwanted extra gap - hence the `!pt-0`/`!pb-0` overrides below. Content
 * doesn't expose a prop to opt out of this per side, so it's overridden
 * here from the circle side with an `!important` descendant rule rather
 * than patching Compass.
 *
 * Returns `null` when no override is needed. The caller still always
 * renders its wrapper element regardless, varying only its className,
 * rather than conditionally rendering the wrapper itself, keeping the tree
 * shape stable across renders so toggling an override doesn't remount
 * everything beneath it.
 */
export const getMobileMainWrapperClassName = ({
  isTopHidden,
  isBottomHidden,
}: GetMobileMainWrapperClassNameArgs): string | null => {
  const overrides = [
    isTopHidden && "[&_main]:!pt-0",
    isBottomHidden && "[&_main]:!pb-0",
  ].filter((token): token is string => Boolean(token));

  return overrides.length > 0 ? overrides.join(" ") : null;
};
